import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Badge,
  Card,
  type CardProps,
  type ColProps,
  Flex,
  type FlexProps,
  Skeleton,
} from 'antd';
import type { RibbonProps } from 'antd/es/badge/Ribbon';
import isUndefined from 'lodash/isUndefined';
import { PosColumn } from 'pos-client/components/shared/Layout/Grid/PosColumn.tsx';
import { useTheme } from 'pos-client/theme/ThemeContext.ts';

import { PosCardHeader, type PosCardHeaderProps } from './PosCardHeader.tsx';

import './PosCard.css';

/**
 * @description Displays accent bar at the bottom of the card
 */
interface PosCardAccentBarProps {
  /** Background color of the accent bar */
  color: string;
  style?: CSSProperties;
}

/**
 * @description Enables expand / collapse behaviour for information displayed in the card body
 */
export interface PosCardExpandProps {
  /** Default expanded on render */
  defaultExpanded?: boolean;
  /** Set exapnded state - _(Automatically handled if undefined)_ */
  expanded?: boolean;
  /**
   * Callback when expanded state is changed
   * @param expanded - New expanded state
   */
  onChange?: (expanded: boolean) => void;
  /** Disable expand / collapse button */
  disabled?: boolean;
  /** Display 'Show More' or 'Show Less' text */
  showText?: boolean;
  /** Override component control of expand / collapse behaviour @remarks Must be used in conjuction with `onChange` & `expanded` props */
  controlExpand?: boolean;
  /** Inline expand / collapse button in card title */
  expandInTitle?: boolean;
}

/**
 * Props for {@link PosCard}
 */
export interface PosCardProps
  extends Omit<CardProps, 'actions' | 'title' | 'headStyle'> {
  /** @see {@link PosCardHeaderProps} */
  headerProps?: PosCardHeaderProps;
  /** Component to display in the top right of the card header */
  action?: ReactNode;
  /** Fill height to 100% of the parent element */
  fullHeight?: boolean;
  /** Enable card drop-shadow @defaultValue true */
  elevate?: boolean;
  /** Card background color */
  cardColor?: string;
  /** Card body flex props @see {@link FlexProps} */
  flexProps?: Omit<FlexProps, 'children'>;
  /** Wrap card in `PosColumn` component @see {@link PosColumn} */
  colProps?: ColProps;
  /** Loading state of the card @defaultValue false */
  loading?: boolean;
  /** Card ribbon props @see {@link RibbonProps} */
  ribbon?: RibbonProps;
  /** Card expand / collapse behaviour @see {@link PosCardExpandProps} */
  expand?: PosCardExpandProps;
  /** Card accent bar props @see {@link PosCardAccentBarProps} */
  accentBar?: PosCardAccentBarProps;
}

const PosCardContent = ({
  fullHeight,
  elevate = true,
  headerProps,
  cardColor,
  loading = false,
  flexProps,
  ribbon,
  children,
  expand,
  accentBar,
  ...rest
}: PosCardProps) => {
  const { darkMode } = useTheme();
  const [expanded, setExpanded] = useState(
    expand?.expanded ?? expand?.defaultExpanded
  );

  const expandable = !!expand;
  useEffect(() => {
    if (
      expandable &&
      !isUndefined(expand.expanded) &&
      expand.expanded !== expanded
    ) {
      setExpanded(expand.expanded);
    }
  }, [expand?.expanded, expandable, expanded]);

  const onExpand = useCallback(
    (value: boolean) => {
      if (expand?.onChange) {
        expand.onChange(value);
      }
      setExpanded((current) => !current);
    },
    [expand]
  );

  const accentBarStyle: CSSProperties = {
    borderBottomWidth: 10,
    borderBottomStyle: 'solid',
    borderBottomColor: accentBar?.color,
  };

  return (
    <Card
      style={{
        ...(fullHeight && { height: '100%' }),
        ...(cardColor && { backgroundColor: cardColor }),
        ...(elevate && {
          boxShadow: darkMode
            ? 'rgb(0 0 0) -1px 3px 5px 0px'
            : '0 4px 14px 0 rgba(220, 225, 232, 0.5)',
        }),
        border: 'none',
        width: '100%',
        ...(accentBar && accentBarStyle),
      }}
      rootClassName={fullHeight ? 'card-full-height' : undefined}
      {...rest}
    >
      <Flex style={{ width: '100%' }} vertical>
        {headerProps && (
          <PosCardHeader
            {...headerProps}
            {...(expandable && {
              expand: {
                expanded,
                onChange: onExpand,
                controlExpand: expand.controlExpand,
                showText: expand.showText,
                disabled: expand.disabled,
                expandInTitle: expand.expandInTitle,
              },
            })}
          />
        )}
        <Skeleton loading={loading} active>
          <Flex
            style={{ width: '100%' }}
            vertical={flexProps?.vertical ?? true}
            {...flexProps}
            hidden={expandable && !expand?.controlExpand && !expanded}
          >
            {children}
          </Flex>
        </Skeleton>
      </Flex>
    </Card>
  );
};
/**
 * @component
 * @description Card to display content of a single entity
 *
 * @see {@link https://ant.design/components/card | AntD Card Component}
 * @param props {@link PosCardProps}
 * @example
 * ```tsx
 * const { t } = useTranslation();
 * <PosCard
 *   title={t('orders.order')}
 *   icon={<FontAwesomeIcon icon={faClipboardList} />}
 * >
 *   <Text>Example Display Text</Text>
 * </PosCard>
 *
 * // As a responsive column
 * import { PosGrid } from 'pos-client/components/shared/Layout/PosGrid.tsx';
 *
 * <PosGrid>
 *   <PosCard
 *     title={t('orders.order')}
 *     icon={<FontAwesomeIcon icon={faClipboardList} />}
 *     colProps={{
 *       span: 24,
 *       md: 12
 *     }}
 *    >
 *     <Text>Example Display Text</Text>
 *   </PosCard>
 * </PosGrid>
 *
 * // Expand / Collapse
 * <PosCard
 *   title={t('orders.order')}
 *   icon={<FontAwesomeIcon icon={faClipboardList} />}
 *   expand={{
 *     showText: true
 *   }}
 * >
 *   <Text>Example Display Text</Text>
 * </PosCard>
 * ```
 */
export const PosCard = ({ colProps, ribbon, ...rest }: PosCardProps) => {
  const cardContent = useMemo(
    () =>
      ribbon ? (
        <Badge.Ribbon {...ribbon}>{<PosCardContent {...rest} />}</Badge.Ribbon>
      ) : (
        <PosCardContent {...rest} />
      ),
    [rest, ribbon]
  );

  return colProps ? (
    <PosColumn {...colProps}>{cardContent}</PosColumn>
  ) : (
    cardContent
  );
};
